/**
 * Quiz Results Page: Advanced Data Collection & Personalization
 * Behavioral tracking, progressive profiling, predictive analytics
 */

(function() {
  'use strict';

  /**
   * Track behavioral data
   */
  function trackBehavioralData() {
    const behaviorData = {
      scrollDepth: 0,
      timeOnPage: 0,
      clickPatterns: [],
      hoverPatterns: [],
      formInteractions: [],
      exitIntent: false
    };

    const startTime = Date.now();

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', function() {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      maxScroll = Math.max(maxScroll, scrollPercent);
      behaviorData.scrollDepth = Math.round(maxScroll);
    }, { passive: true });

    // Track clicks
    document.addEventListener('click', function(e) {
      const target = e.target.closest('[data-trackable]') || e.target.closest('button, a');
      if (target) {
        behaviorData.clickPatterns.push({
          element: target.tagName,
          text: target.textContent?.trim().substring(0, 50),
          timestamp: Date.now() - startTime
        });
      }
    });

    // Track hovers (for interest detection)
    document.querySelectorAll('[data-product-link], [data-results-add-to-cart]').forEach(el => {
      el.addEventListener('mouseenter', function() {
        behaviorData.hoverPatterns.push({
          element: this.dataset.productLink || 'add-to-cart',
          timestamp: Date.now() - startTime
        });
      });
    });

    // Track form interactions
    document.querySelectorAll('input, select, textarea').forEach(el => {
      el.addEventListener('focus', function() {
        behaviorData.formInteractions.push({
          field: this.name || this.id,
          timestamp: Date.now() - startTime
        });
      });
    });

    // Track exit intent
    document.addEventListener('mouseleave', function(e) {
      if (e.clientY <= 0) {
        behaviorData.exitIntent = true;
      }
    });

    // Save on page unload
    window.addEventListener('beforeunload', function() {
      behaviorData.timeOnPage = Math.floor((Date.now() - startTime) / 1000);
      localStorage.setItem('veluna_behavior_data', JSON.stringify(behaviorData));
    });
  }

  /**
   * Progressive profiling (collect more data over time)
   */
  function progressiveProfiling() {
    const profile = JSON.parse(localStorage.getItem('veluna_user_profile') || '{}');
    
    // Collect quiz data
    const quizResults = sessionStorage.getItem('veluna_quiz_results');
    if (quizResults) {
      try {
        const results = JSON.parse(quizResults);
        // Raw quiz answers are health-adjacent; they stay in sessionStorage
        // and are deliberately NOT persisted to localStorage.
        profile.recommendedCategory = results.category;
        profile.recommendedBundle = results.bundle;
      } catch (e) {
        console.warn('Failed to parse quiz results:', e);
      }
    }

    // Collect behavioral data
    const behaviorData = JSON.parse(localStorage.getItem('veluna_behavior_data') || '{}');
    if (behaviorData.timeOnPage) {
      profile.engagementLevel = behaviorData.scrollDepth > 75 ? 'high' : behaviorData.scrollDepth > 50 ? 'medium' : 'low';
      profile.timeOnPage = behaviorData.timeOnPage;
    }

    // Collect device/browser info
    // Device fingerprint removed: userAgent + screen dimensions were collected
    // with no stated purpose limitation. Viewport width alone is enough for layout.
    profile.viewportWidth = window.innerWidth;

    // Save updated profile
    localStorage.setItem('veluna_user_profile', JSON.stringify(profile));
  }

  /**
   * Generate personalized recommendations based on behavior
   */
  function generatePersonalizedRecommendations() {
    const profile = JSON.parse(localStorage.getItem('veluna_user_profile') || '{}');
    const behaviorData = JSON.parse(localStorage.getItem('veluna_behavior_data') || '{}');

    // Determine user intent
    let intent = 'browsing';
    if ((behaviorData.clickPatterns || []).some(c => c.element === 'BUTTON' && c.text.includes('Add to Cart'))) {
      intent = 'high_intent';
    } else if ((behaviorData.hoverPatterns || []).length > 3) {
      intent = 'considering';
    } else if (behaviorData.scrollDepth > 75) {
      intent = 'engaged';
    }

    // Generate recommendations
    const recommendations = {
      intent: intent,
      suggestedActions: [],
      personalizedMessage: ''
    };

    if (intent === 'browsing') {
      recommendations.suggestedActions.push('View product details');
      recommendations.suggestedActions.push('Read customer reviews');
      recommendations.personalizedMessage = 'Take your time exploring your personalized results';
    } else if (intent === 'considering') {
      recommendations.suggestedActions.push('Compare with other sets');
      recommendations.suggestedActions.push('Chat with Luna for questions');
      recommendations.personalizedMessage = 'You\'re considering the perfect set for your needs';
    } else if (intent === 'high_intent') {
      recommendations.suggestedActions.push('Complete purchase');
      recommendations.suggestedActions.push('Add subscription for savings');
      recommendations.personalizedMessage = 'You\'re ready to transform your sleep';
    }

    return recommendations;
  }

  /**
   * Track for predictive analytics
   */
  function trackForPredictiveAnalytics() {
    const urlParams = new URLSearchParams(window.location.search);
    const category = urlParams.get('category');
    const bundle = urlParams.get('bundle');

    const analyticsData = {
      category: category,
      bundle: bundle,
      timestamp: Date.now(),
      sessionId: sessionStorage.getItem('veluna_session_id') || `session_${Date.now()}`,
      userProfile: JSON.parse(localStorage.getItem('veluna_user_profile') || '{}'),
      behavior: JSON.parse(localStorage.getItem('veluna_behavior_data') || '{}')
    };

    // Send to analytics (would be API call in production)
    if (window.VelunaQuizResultsCart && window.VelunaQuizResultsCart.trackEvent) {
      window.VelunaQuizResultsCart.trackEvent('predictive_analytics', analyticsData);
    }

    // Store for ML model training
    const analyticsHistory = JSON.parse(localStorage.getItem('veluna_analytics_history') || '[]');
    analyticsHistory.push(analyticsData);
    
    // Retention: keep at most 30 days and at most 100 entries.
    var cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
    while (analyticsHistory.length && analyticsHistory[0].timestamp < cutoff) {
      analyticsHistory.shift();
    }
    while (analyticsHistory.length > 100) {
      analyticsHistory.shift();
    }
    
    localStorage.setItem('veluna_analytics_history', JSON.stringify(analyticsHistory));
  }

  /**
   * Initialize data collection
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Generate session ID if not exists
    if (!sessionStorage.getItem('veluna_session_id')) {
      sessionStorage.setItem('veluna_session_id', `session_${Date.now()}`);
    }

    // Track behavioral data
    trackBehavioralData();

    // Progressive profiling
    progressiveProfiling();

    // Generate personalized recommendations
    const recommendations = generatePersonalizedRecommendations();

    // Track for predictive analytics
    trackForPredictiveAnalytics();

    console.log('Quiz Results Data Collection: Tracking initialized');
  }

  init();

  // Export
  if (typeof window !== 'undefined') {
    window.VelunaQuizResultsDataCollection = {
      trackBehavioralData,
      progressiveProfiling,
      generatePersonalizedRecommendations,
      trackForPredictiveAnalytics
    };
  }
})();









